import * as Sentry from "@sentry/react-native";
import { useQuery } from "@tanstack/react-query";
import { router } from "expo-router";
import { observer } from "mobx-react";
import React from "react";
import { FormattedMessage } from "react-intl";
import { Spacer } from "~/components/Spacer";
import { ButtonV2 } from "~/components/V2/Button";
import { Card } from "~/components/V2/Card";
import { ErrorViewV2 } from "~/components/V2/ErrorViewV2";
import { SpinnerV2 } from "~/components/V2/SpinnerV2";
import { BodyText } from "~/components/V2/Text/BodyText";
import { BrandText } from "~/components/V2/Text/BrandText";
import { View } from "~/components/V2/View";
import { useErrorAlertV2 } from "~/components/V2/error-alert-v2";
import { useEvent } from "~/hooks/useEvent";
import type { ClinicInstance } from "~/mobx/entities/clinics/Clinic";
import { useStore } from "~/mobx/utils/useStore";
import { styleConstantsV2 } from "~/style/styleConstantsV2";
import { bookingDataLayerPush } from "~/utils/logBookingEvent";
import { useProgressBarStep } from "./useProgressBarStep";
import { useStandaloneBookingFlow } from "./useStandaloneBookingFlow";

function generateFullAddress(clinic: ClinicInstance) {
  const shouldShowComma = !!clinic.streetAddress;
  const clinicStreetAddress = clinic.streetAddress ?? "";
  const clinicZipCode = clinic.zipCode ?? "";
  const clinicCity = clinic.city ?? "";
  const canAddressBeConstructed =
    !!clinicStreetAddress || !!clinicZipCode || !!clinicCity;
  return canAddressBeConstructed
    ? `${clinicStreetAddress}${shouldShowComma ? ", " : ""}${clinicZipCode} ${clinicCity}`
    : "";
}

interface SelectClinicScreenProps {
  clinicFromURL?: ClinicInstance;
  numberOfPets?: number;
}
const SelectClinicScreen = observer(function SelectClinicScreen({
  clinicFromURL,
  numberOfPets = 0,
}: SelectClinicScreenProps) {
  const errorAlert = useErrorAlertV2();
  const store = useStore();
  const { isStandAloneBookingFlow, clinicId, isNewUser } =
    useStandaloneBookingFlow();

  useProgressBarStep(1);

  const navigateToNextScreen = useEvent(
    async ({
      selectedClinicId,
      selectedClinicName,
    }: {
      selectedClinicId: string;
      selectedClinicName: string;
    }) => {
      // Save the selected clinic ID so we can make the appointment with it
      store.bookingStore.setClinicId(selectedClinicId);
      // Save clinic name so we can use it for tracking with GTM later in SuccessScreen
      store.bookingStore.setClinicName(selectedClinicName);

      if (isStandAloneBookingFlow) {
        // Log the event to the booking data layer
        bookingDataLayerPush({
          event: "Booking_web_app",
          ga_action: "Choose clinic",
          ga_label: selectedClinicName,
        });

        if (isNewUser || numberOfPets === 0) {
          router.navigate({
            pathname: "/(standalone)/booking/add-new-pet",
            params: {
              clinicId: selectedClinicId,
              isNewUser: isNewUser ? "1" : "0",
            },
          });
        } else {
          router.navigate({
            pathname: "/(standalone)/booking/pet",
            params: {
              clinicId: selectedClinicId,
              isNewUser: isNewUser ? "1" : "0",
            },
          });
        }
      } else {
        if (numberOfPets === 0) {
          router.navigate({
            pathname: "/(app)/book-appointment/add-new-pet",
          });
        } else {
          router.navigate({
            pathname: "/(app)/book-appointment/pet",
          });
        }
      }
    },
  );

  const query = useQuery({
    queryKey: ["clinics"],
    queryFn: async () => {
      const clinics = await store.clinicsStore.getRecommendedClinicList();

      return clinics;
    },
    enabled: isNewUser === false,
  });

  if (query.isLoading) {
    return (
      <View flex colorWhite>
        <View padding32 centerContent>
          <SpinnerV2 />
        </View>
      </View>
    );
  }

  if (query.isError) {
    return (
      <View flex colorWhite>
        <View centerContent paddingHorizontal24>
          <ErrorViewV2
            error={query.error}
            title={intl.formatMessage({
              id: "m4dnTD",
              defaultMessage: "Something went wrong",
              description:
                "Error message title when fetching clinic list fails",
            })}
          />
          <ButtonV2
            onPress={query.refetch}
            title={intl.formatMessage({
              id: "ghblYR",
              defaultMessage: "Try Again!",
              description: "Button to retry fetching clinic list",
            })}
          />
        </View>
      </View>
    );
  }

  const isClinicFromURLIncludedInRecommendedClinics = !!query.data?.some(
    (clinic) => clinic.id === clinicFromURL?.id,
  );

  return (
    <View paddingHorizontal16 flex colorWhite>
      <View paddingVertical40 alignItemsCenter>
        <BrandText sizeH1 weightExtraBold colorBlack>
          <FormattedMessage
            id="3QGdyf"
            defaultMessage="Choose a clinic"
            description="Title on the select clinic screen in booking flow"
          />
        </BrandText>
      </View>

      {/* We don't want to show the clinic from URL if it is already included in the recommended clinics */}
      {clinicFromURL &&
        isClinicFromURLIncludedInRecommendedClinics === false && (
          <Card
            key={clinicFromURL.id}
            title={clinicFromURL.name}
            subtitle={generateFullAddress(clinicFromURL)}
            onPress={async () => {
              try {
                await navigateToNextScreen({
                  selectedClinicId: clinicFromURL.id,
                  selectedClinicName: clinicFromURL.name,
                });
              } catch (error) {
                console.log("Error while navigating to next screen", error);
                errorAlert(error);
                Sentry.captureException(error);
              }
            }}
          />
        )}

      <Spacer extraExtraLarge />
      {(query.data?.length ?? 0) > 0 && (
        <>
          <BodyText sizeLarge colorBlack weightRegular>
            <FormattedMessage
              id="1bZ2OG"
              defaultMessage="Last visited"
              description="Last visited clinic title"
            />
          </BodyText>
          <Spacer small />

          <View style={{ gap: styleConstantsV2.spacing16 }}>
            {query.data?.map((clinic) => {
              return (
                <Card
                  key={clinic.id}
                  title={clinic.name}
                  subtitle={generateFullAddress(clinic)}
                  onPress={async () => {
                    try {
                      await navigateToNextScreen({
                        selectedClinicId: clinic.id,
                        selectedClinicName: clinic.name,
                      });
                    } catch (error) {
                      console.log(
                        "Error while navigating to next screen",
                        error,
                      );
                      errorAlert(error);
                      Sentry.captureException(error);
                    }
                  }}
                />
              );
            })}
          </View>
          <Spacer extraLarge />
        </>
      )}

      <ButtonV2
        centerContent
        small
        title={intl.formatMessage({
          id: "L26XQU",
          defaultMessage: "Select another clinic",
          description: "Button to select another clinic",
        })}
        secondary
        style={{ width: 234, alignSelf: "center" }}
        onPress={() => {
          if (isStandAloneBookingFlow) {
            // Log the event to the booking data layer
            bookingDataLayerPush({
              event: "Booking_web_app",
              ga_action: "Choose another clinic",
            });

            router.navigate({
              pathname: "/(standalone)/booking/search-clinic",
              params: {
                clinicId,
                isNewUser: isNewUser ? "1" : "0",
              },
            });
          } else {
            router.navigate({
              pathname: "/(app)/book-appointment/search-clinic",
            });
          }
        }}
      />
      <Spacer extraExtraLarge />
    </View>
  );
});

export const HandleClinicFromURLScreen = observer(
  function HandleClinicFromURLScreen() {
    const store = useStore();

    const { clinicId } = useStandaloneBookingFlow();

    const query = useQuery({
      queryKey: ["clinic", clinicId],
      queryFn: async () => {
        const clinic = await store.clinicsStore.getClinic(clinicId);
        return clinic;
      },
      // clinicId 0 means no clinic is selected
      enabled: !!clinicId && clinicId !== "0",
    });

    if (query.isLoading) {
      return (
        <View flex colorWhite>
          <View padding32 centerContent>
            <SpinnerV2 />
          </View>
        </View>
      );
    }

    if (query.isError) {
      return (
        <View flex colorWhite>
          <View centerContent paddingHorizontal24>
            <ErrorViewV2
              error={query.error}
              title={intl.formatMessage({
                id: "m4dnTD",
                defaultMessage: "Something went wrong",
                description:
                  "Error message title when fetching clinic list fails",
              })}
            />
            <ButtonV2
              onPress={query.refetch}
              title={intl.formatMessage({
                id: "ghblYR",
                defaultMessage: "Try Again!",
                description: "Button to retry fetching clinic list",
              })}
            />
          </View>
        </View>
      );
    }

    return <FetchMyPetsScreen clinicFromURL={query.data} />;
  },
);

interface FetchMyPetsScreenProps {
  clinicFromURL?: ClinicInstance;
}

const FetchMyPetsScreen = observer(function FetchMyPetsScreen({
  clinicFromURL,
}: FetchMyPetsScreenProps) {
  const store = useStore();

  const activeUser = store.authStore.activeUser;

  const { isNewUser } = useStandaloneBookingFlow();

  const query = useQuery({
    queryKey: ["myPets", activeUser?.id],
    queryFn: async () => {
      const pets = await store.petsStore.getMyPets();
      return pets;
    },
    enabled: isNewUser === false,
  });

  if (query.isLoading) {
    return (
      <View flex colorWhite>
        <View padding32 centerContent>
          <SpinnerV2 />
        </View>
      </View>
    );
  }

  if (query.isError) {
    return (
      <View flex colorWhite>
        <View centerContent paddingHorizontal24>
          <ErrorViewV2
            error={query.error}
            title={intl.formatMessage({
              id: "pK+h9Q",
              defaultMessage: "Something went wrong",
              description:
                "Error message title when fetching my pets list fails",
            })}
          />
          <ButtonV2
            onPress={query.refetch}
            title={intl.formatMessage({
              id: "D6szQG",
              defaultMessage: "Try Again!",
              description: "Button to retry fetching my pets list",
            })}
          />
        </View>
      </View>
    );
  }

  return (
    <SelectClinicScreen
      clinicFromURL={clinicFromURL}
      numberOfPets={query.data?.length}
    />
  );
});
